import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { useCreditDialogOpen } from '@/components/CreditDialog/useCreditDialog';
import DialogStage from '@/components/DialogStage';
import { useAccountModals } from '@/components/Modals/Account/context';
import { useHasBeenOpened } from '@/components/Modals/hooks/useHasBeenOpened';
import { AccountModalNames, ModalConversionPoints } from '@/components/Modals/shared-types';
import { SignUpModalScreenName } from '@/components/Modals/SignUp/types';
import { subscribeModalInitialScreenOverrideAtom, subscribeModalMinimumPlanAtom, subscribeModalOpenAtom, subscribeModalSelectedPlanAtom } from '@/components/Modals/Subscribe/atoms';
import { assetPaywallUpgradeModalOpenAtom, assetPaywallUpgradeModalPlanAtom } from '@/components/Modals/UpgradePlan/atoms';
import type { AccountModalsContextType, ModalProps } from '@/components/Modals/shared-types';

// Re-exporting for backward compatibility
export { AccountModalNames, ModalConversionPoints };
export type { AccountModalsContextType, ModalProps };

// Every modal body below is code-split (DEV-6485): `src/components/Modals`
// was 172 KB gz of the `_app` bundle, downloaded and parsed by every visitor
// even though most never open one. `ssr: false` because none of these render
// on the server — they're all closed on first paint — and each is paired with
// `useHasBeenOpened` so the chunk is only requested once the modal opens.
// Types survive the split: `dynamic()` infers props from the `import()`.
const SignUpModal = dynamic(() => import('@/components/Modals/SignUp'), {
  ssr: false
});
const LoginModal = dynamic(() => import('@/components/Modals/Login'), {
  ssr: false
});
const VerifyEmailModal = dynamic(() => import('@/components/Modals/VerifyEmail'), {
  ssr: false
});
const SubscribeModal = dynamic(() => import('@/components/Modals/Subscribe'), {
  ssr: false
});
const UpgradePlanModal = dynamic(() => import('@/components/Modals/UpgradePlan/modal'), {
  ssr: false
});
// The post-download Uppbeat credit dialog. Mounted here (not per-page) so the
// download journey's usage step can open it after the stage closes (DEV-6627);
// download history opens the same dialog through the shared atom.
const CreditDialog = dynamic(() => import('@/components/CreditDialog/CreditDialog'), {
  ssr: false
});
const AccountModals = () => {
  const {
    currentModal,
    handleOpenChange,
    initialScreen,
    modalProps
  } = useAccountModals();
  const initialSignUpModalScreen = useMemo(() => {
    if (currentModal === AccountModalNames.SignUpCreator) {
      return SignUpModalScreenName.SignUpCreator;
    }
    if (currentModal === AccountModalNames.SignUpFree) {
      return SignUpModalScreenName.SignUpFree;
    }
    return SignUpModalScreenName.SignUpAllPlans;
  }, [currentModal]);
  const [subscribeModalOpen, setSubscribeModalOpen] = useAtom(subscribeModalOpenAtom);
  const setSelectedPlan = useSetAtom(subscribeModalSelectedPlanAtom);
  const setMinimumPlan = useSetAtom(subscribeModalMinimumPlanAtom);
  const setInitialScreenOverride = useSetAtom(subscribeModalInitialScreenOverrideAtom);
  const [assetPaywallUpgradeModalOpen, setAssetPaywallUpgradeModalOpen] = useAtom(assetPaywallUpgradeModalOpenAtom);
  const assetPaywallUpgradeModalPlan = useAtomValue(assetPaywallUpgradeModalPlanAtom);
  const signUpModalOpen = currentModal === AccountModalNames.SignUp || currentModal === AccountModalNames.SignUpCreator || currentModal === AccountModalNames.SignUpFree || currentModal === AccountModalNames.SignUpAllPlans;
  const loginModalOpen = currentModal === AccountModalNames.Login;
  const verifyEmailModalOpen = currentModal === AccountModalNames.VerifyEmail;
  const creditDialogOpen = useCreditDialogOpen();
  const renderSignUpModal = useHasBeenOpened(signUpModalOpen);
  const renderLoginModal = useHasBeenOpened(loginModalOpen);
  const renderVerifyEmailModal = useHasBeenOpened(verifyEmailModalOpen);
  const renderSubscribeModal = useHasBeenOpened(subscribeModalOpen);
  const renderUpgradePlanModal = useHasBeenOpened(assetPaywallUpgradeModalOpen);
  const renderCreditDialog = useHasBeenOpened(creditDialogOpen);
  return <>
      {!!renderSignUpModal && <SignUpModal initial={initialScreen as SignUpModalScreenName | undefined ?? initialSignUpModalScreen} onOpenChange={handleOpenChange} open={signUpModalOpen} welcomeMessageType={modalProps?.welcomeMessageType ?? undefined} />}
      {!!renderLoginModal && <LoginModal initial={initialScreen} onOpenChange={handleOpenChange} open={loginModalOpen} />}
      <DialogStage data-sentry-element="DialogStage" data-sentry-source-file="index.tsx" />
      {!!renderVerifyEmailModal && <VerifyEmailModal onOpenChange={handleOpenChange} open={verifyEmailModalOpen} variant="download" />}
      {!!renderSubscribeModal && <SubscribeModal onOpenChange={open => {
      setSubscribeModalOpen(open);
      if (!open) {
        // After the modal has animated out, we can safely reset the selected plan
        setTimeout(() => {
          setSelectedPlan(null);
          setMinimumPlan(null);
          setInitialScreenOverride(null);
        }, 300);
      }
    }} open={subscribeModalOpen} />}
      {!!renderUpgradePlanModal && <UpgradePlanModal onOpenChange={setAssetPaywallUpgradeModalOpen} open={assetPaywallUpgradeModalOpen} plan={assetPaywallUpgradeModalPlan} />}
      {!!renderCreditDialog && <CreditDialog />}
    </>;
};
export { AccountModals };
export { AccountModalsProvider, useAccountModals } from '@/components/Modals/Account/context';